import { Link } from 'react-router-dom'
import type { StoredSession } from '../api/session.js'
import { LEGAL, LegalShell, Mail, TranslationNotice } from './LegalShell.js'

const NOTICE_BEFORE = 'Esta traducción al español se ofrece para tu comodidad. En caso de discrepancia, la'
const NOTICE_LINK = 'versión francesa'
const NOTICE_AFTER = 'es la que prevalece.'

export function LegalNoticeEs({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="Información legal" title="Aviso legal">
      <TranslationNotice original="/mentions-legales" before={NOTICE_BEFORE} link={NOTICE_LINK} after={NOTICE_AFTER} />

      <h2>Editor del sitio</h2>
      <p>
        El sitio {LEGAL.domain} («{LEGAL.siteName}») lo edita un particular, a título no profesional. De conformidad con el artículo 6
        III-2 de la ley francesa n.º 2004-575, de 21 de junio de 2004, para la confianza en la economía digital, el editor no publica sus
        datos personales; estos se han comunicado al proveedor de alojamiento.
      </p>
      <p>
        Contacto: <Mail />
      </p>

      <h2>Alojamiento</h2>
      <p>
        El sitio está alojado por {LEGAL.host.name}, {LEGAL.host.address.es} —{' '}
        <a href={LEGAL.host.website} rel="noopener noreferrer" target="_blank">
          {LEGAL.host.website.replace('https://', '')}
        </a>
        .
      </p>

      <h2>Propiedad intelectual</h2>
      <p>
        El código, las ilustraciones, el logotipo y los textos del sitio son propiedad del editor, salvo que se indique lo contrario. El motor
        de ajedrez se basa en la biblioteca libre chess.js (licencia BSD-2-Clause); las fuentes Fraunces e Instrument Sans se distribuyen bajo
        la licencia SIL Open Font License.
      </p>
      <p>
        Chess-Uno es un juego independiente. No está afiliado, avalado ni patrocinado por Mattel, titular de la marca UNO®.
      </p>

      <h2>Datos personales</h2>
      <p>
        El tratamiento de los datos de los jugadores se describe en la <Link to="/es/privacidad">política de privacidad</Link>.
      </p>
    </LegalShell>
  )
}

export function PrivacyEs({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="Tus datos" title="Política de privacidad">
      <TranslationNotice original="/confidentialite" before={NOTICE_BEFORE} link={NOTICE_LINK} after={NOTICE_AFTER} />
      <p className="lead-p">
        {LEGAL.siteName} recoge solo lo estrictamente necesario para que puedas jugar y aparecer en la clasificación. Sin publicidad, sin
        herramientas de medición de audiencia de terceros, sin cookies de seguimiento.
      </p>

      <h2>Responsable del tratamiento</h2>
      <p>
        El editor del sitio, un particular (ver el <Link to="/es/aviso-legal">aviso legal</Link>), localizable en <Mail />.
      </p>

      <h2>Datos tratados</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Dato</th>
              <th>Uso</th>
              <th>Conservación</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Nombre de usuario</td>
              <td>Identificar tu cuenta y mostrarte a los demás jugadores.</td>
              <td>Hasta la eliminación de la cuenta.</td>
            </tr>
            <tr>
              <td>Contraseña</td>
              <td>Inicio de sesión. Nunca se almacena en claro: solo se conserva una huella con sal (scrypt).</td>
              <td>Hasta la eliminación de la cuenta.</td>
            </tr>
            <tr>
              <td>Tokens de sesión</td>
              <td>
                Mantener la sesión iniciada. El token se guarda en el almacenamiento local de tu navegador; el servidor solo conserva una huella de él.
              </td>
              <td>Hasta el cierre de sesión o la eliminación de la cuenta.</td>
            </tr>
            <tr>
              <td>Partidas</td>
              <td>Movimientos, cartas robadas y registro de cada partida, para reanudarla tras una desconexión y para el arbitraje.</td>
              <td>Hasta la eliminación de la cuenta.</td>
            </tr>
            <tr>
              <td>Resultados clasificatorios</td>
              <td>Clasificación Elo, estadísticas, historial de enfrentamientos directos.</td>
              <td>Hasta la eliminación de la cuenta; después se conservan sin tu nombre de usuario para no falsear la clasificación de los demás.</td>
            </tr>
            <tr>
              <td>Torneos</td>
              <td>Nombre del torneo, nombres de usuario de los participantes, cuadro y resultados de los enfrentamientos, para organizar el torneo.</td>
              <td>Hasta la eliminación de la cuenta.</td>
            </tr>
            <tr>
              <td>Opiniones y sugerencias</td>
              <td>
                Lo que decidas enviar: mensaje, nota del 1 al 5, contacto opcional (correo electrónico o usuario de mensajería), así como tu nombre de
                usuario si has iniciado sesión, la página, el idioma, el tipo de pantalla y el navegador utilizados. Las lee el editor para mejorar el
                juego y responderte si lo pides.
              </td>
              <td>12 meses, o hasta que solicites su eliminación.</td>
            </tr>
            <tr>
              <td>Origen de las visitas</td>
              <td>
                Saber si los visitantes llegan desde Instagram, Google u otro lugar: sitio de procedencia (solo el nombre de dominio), etiquetas de
                campaña del enlace, página de llegada sin parámetros ni códigos de invitación, idioma, tipo de pantalla y si la visita llevó a crear
                una cuenta. No se registra ninguna dirección IP, ningún dato detallado del navegador ni ningún identificador duradero, y nada se cruza
                con tu cuenta. No se mide nada si tu navegador envía «No rastrear» (Do Not Track) o la señal Global Privacy Control.
              </td>
              <td>13 meses.</td>
            </tr>
            <tr>
              <td>Dirección IP</td>
              <td>
                Limitar los intentos de inicio de sesión y el envío de opiniones (protección contra ataques). Se mantiene en memoria y el juego nunca
                la registra.
              </td>
              <td>Un minuto para el inicio de sesión, diez minutos para las opiniones. Los registros técnicos del proveedor de alojamiento siguen su propia política.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Bases jurídicas</h2>
      <ul>
        <li>
          <strong>Ejecución del servicio</strong> que solicitas al crear una cuenta: cuenta, partidas, clasificación.
        </li>
        <li>
          <strong>Interés legítimo</strong>: seguridad del servicio (limitación de los intentos de inicio de sesión y del envío de opiniones),
          mejora del juego a partir de las opiniones que decidas enviar y medición estadística del origen de las visitas. Puedes oponerte en
          cualquier momento activando «No rastrear» (Do Not Track) en tu navegador.
        </li>
      </ul>

      <h2>Lo que es público</h2>
      <p>
        Tu nombre de usuario, tu puntuación Elo y tu nivel, tus estadísticas, tus resultados clasificatorios y tus enfrentamientos directos
        aparecen en la clasificación y en tu perfil, visibles para todos. Las partidas contra la IA no son clasificatorias. El nombre de un
        torneo, los nombres de usuario de sus participantes, su cuadro y sus resultados son visibles para cualquier persona que conozca su
        código. Tus opiniones nunca se publican.
      </p>

      <h2>Almacenamiento en tu navegador</h2>
      <p>
        El sitio no utiliza cookies. Guarda en el almacenamiento local del navegador tu token de sesión, para mantener tu sesión iniciada, así
        como tus preferencias de visualización (idioma, sonido) y un pequeño contador que evita pedirte tu opinión con demasiada frecuencia
        después de una partida: este almacenamiento es estrictamente necesario para el servicio y, por lo tanto, no requiere consentimiento. El
        almacenamiento de sesión de la pestaña guarda, hasta que se cierra, un identificador aleatorio de la visita para contarla una sola vez;
        no se conserva de una visita a otra.
      </p>

      <h2>Destinatarios y encargados del tratamiento</h2>
      <p>Tus datos no se venden ni se comparten con fines comerciales. Los tratan:</p>
      <ul>
        <li>
          <strong>{LEGAL.host.name}</strong> (Estados Unidos): alojamiento del servidor y de la base de datos;
        </li>
        <li>
          <strong>Cloudflare, Inc.</strong> (Estados Unidos): gestión del nombre de dominio y, en su caso, encaminamiento del tráfico;
        </li>
        <li>
          <strong>Telegram</strong> (mensajería) y <strong>Resend, Inc.</strong> (Estados Unidos, envío de correos electrónicos), en su caso:
          entrega al editor de una notificación de cada opinión recibida.
        </li>
      </ul>
      <p>
        Estas transferencias fuera de la Unión Europea están sujetas a las garantías previstas por el RGPD (cláusulas contractuales tipo de la
        Comisión Europea o Marco de Privacidad de Datos UE-EE. UU., según el proveedor).
      </p>

      <h2>Seguridad</h2>
      <p>
        Conexiones cifradas (HTTPS), contraseñas y tokens almacenados en forma de huellas, limitación de los intentos de inicio de sesión,
        acceso a la base de datos restringido al entorno de alojamiento.
      </p>

      <h2>Tus derechos</h2>
      <p>
        Tienes derecho de acceso, rectificación, supresión, limitación del tratamiento, portabilidad y oposición. Para ejercerlos —en particular
        para eliminar tu cuenta—, escribe a <Mail /> desde una dirección que permita identificarte, indicando tu nombre de usuario. Recibirás una
        respuesta en el plazo de un mes.
      </p>
      <p>
        También puedes presentar una reclamación ante la CNIL, la autoridad francesa de protección de datos (
        <a href="https://www.cnil.fr" rel="noopener noreferrer" target="_blank">
          cnil.fr
        </a>
        ).
      </p>
    </LegalShell>
  )
}
